import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CALENDLY_URL = 'https://calendly.com/admin-dailygrinddesign/30min';

const services = [
  {
    title: 'Promotional Designs',
    description: 'Eye-catching promo designs that get attention and drive results — perfect for events, launches, and campaigns.',
    features: ['Custom Artwork', 'Print & Digital Formats', 'Unlimited Revisions', 'Fast Turnaround'],
    price: '$249',
    image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  },
  {
    title: 'Logo Design',
    description: 'Professional logo design that captures your brand essence and makes a lasting impression on your customers.',
    features: ['Custom Design', 'Multiple Concepts', 'Vector Files', 'Brand Guidelines'],
    price: '$499',
    popular: true,
    image: 'https://images.unsplash.com/photo-1545065695-64b67d551e68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxsb2dvJTIwZGVzaWdufGVufDB8fHxibHVlfDE3NTA0ODczOTF8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    title: 'Website Building',
    description: 'Custom-built websites that turn visitors into loyal, paying customers and scale with your business.',
    features: ['Responsive Design', 'SEO Optimization', 'Content Management', 'Launch Support'],
    price: '$999',
    image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  },
];

const testimonials = [
  { name: 'Sarah Johnson', company: 'TechStart Solutions', text: "The logo design perfectly captured our tech startup's innovative spirit. Our brand recognition increased by 40% after the rebrand!", rating: 5 },
  { name: 'Mike Chen', company: 'Local Bistro', text: 'The promotional designs they created packed our launch night — over 300 walk-ins on day one. Eye-catching work that drives real foot traffic.', rating: 5 },
  { name: 'Lisa Rodriguez', company: 'Fitness Plus', text: 'The website they built completely transformed how members book classes. New sign-ups jumped 60% in the first month after launch.', rating: 5 },
];

const processSteps = [
  { step: '01', title: 'Consultation', description: 'We discuss your vision, goals, and brand requirements to understand your unique needs.' },
  { step: '02', title: 'Concept Development', description: 'Our team creates initial concepts and presents multiple design directions for your review.' },
  { step: '03', title: 'Refinement', description: 'We refine the chosen concept based on your feedback to ensure perfect alignment.' },
  { step: '04', title: 'Delivery', description: 'Final files delivered in all required formats with full ownership and usage rights.' },
];

const useScrollAnimations = () => {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    const elements = document.querySelectorAll('[id]');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return isVisible;
};

const useHashScroll = () => {
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      const timer = setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);
};

const HeroSection = ({ isVisible }) => (
  <section id="hero" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white pt-24 md:pt-20">
    <div className="container mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className={`space-y-8 ${isVisible.hero ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
            Elevate Your
            <span className="bg-gradient-to-r from-red-600 to-black bg-clip-text text-transparent"> Brand</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed">
            Professional design services that transform small businesses and startups into memorable brands that drive growth and success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#contact" className="bg-red-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-red-700 transform hover:scale-105 transition-all shadow-lg hover:shadow-xl inline-block text-center">Get Started Today</a>
            <Link to="/portfolio" data-testid="hero-view-portfolio" className="border-2 border-red-600 text-red-600 px-8 py-4 rounded-xl font-semibold hover:bg-red-600 hover:text-white transition-all inline-block text-center">View Portfolio</Link>
          </div>
          <div className="flex items-center space-x-8 pt-8">
            <div className="text-center"><div className="text-3xl font-bold text-red-600">500+</div><div className="text-gray-600">Projects Completed</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-red-600">98%</div><div className="text-gray-600">Client Satisfaction</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-red-600">24hr</div><div className="text-gray-600">Quick Turnaround</div></div>
          </div>
        </div>
        <div className={`${isVisible.hero ? 'animate-fade-in-right' : 'opacity-0'}`}>
          <div className="relative">
            <img
              src="https://lh3.googleusercontent.com/d/1iybq0GTfkgyHFp2WmIeR5Dqr8wumUFze"
              alt="Daily Grind Design Professional"
              className="rounded-2xl shadow-2xl w-full h-96 object-cover"
              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1577594955964-d50df414b8ff'; }}
            />
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <div><div className="font-semibold">Project Delivered</div><div className="text-sm text-gray-600">On time & on budget</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ServiceCard = ({ service, isVisible, animationDelay }) => (
  <div
    className={`group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col h-full ${service.popular ? 'ring-2 ring-red-600' : ''} ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
    style={{ animationDelay }}
  >
    {service.popular && (
      <div data-testid="badge-most-popular" className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg uppercase tracking-wider whitespace-nowrap">
        Most Popular
      </div>
    )}
    <div className="mb-6">
      <img src={service.image} alt={service.title} className="w-full h-48 object-cover rounded-xl mb-4" />
      <h3 className="text-3xl font-bold text-gray-900 mb-3 text-center">{service.title}</h3>
      <p className="text-gray-600 mb-6">{service.description}</p>
    </div>
    <div className="space-y-3 mb-6 flex-grow">
      {service.features.map((feature) => (
        <div key={feature} className="flex items-center space-x-3">
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          <span className="text-gray-700">{feature}</span>
        </div>
      ))}
    </div>
    <div className="mt-auto">
      <div className="text-3xl font-bold text-red-600 mb-4 text-center">{service.price}</div>
      <a href="#contact" className="w-full bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transform hover:scale-105 transition-all inline-block text-center">Get Started</a>
    </div>
  </div>
);

const ServicesSection = ({ isVisible }) => (
  <section id="services" className="py-20 bg-white">
    <div className="container mx-auto px-6">
      <div className={`text-center mb-16 ${isVisible.services ? 'animate-fade-in-up' : 'opacity-0'}`}>
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Our Expert Services</h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">Comprehensive brand recognition solutions designed specifically for small businesses and startups ready to make their mark.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map((service, idx) => (
          <ServiceCard key={service.title} service={service} isVisible={isVisible.services} animationDelay={`${idx * 0.1}s`} />
        ))}
      </div>
    </div>
  </section>
);

const ProcessStep = ({ step, isVisible, animationDelay }) => (
  <div className={`text-center ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay }}>
    <div className="relative mb-8">
      <div className="w-20 h-20 bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">{step.step}</div>
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
    <p className="text-gray-600">{step.description}</p>
  </div>
);

const ProcessSection = ({ isVisible }) => (
  <section id="process" className="py-20 bg-gray-50">
    <div className="container mx-auto px-6">
      <div className={`text-center mb-16 ${isVisible.process ? 'animate-fade-in-up' : 'opacity-0'}`}>
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Our Proven Process</h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">A streamlined approach that ensures exceptional results and complete client satisfaction every time.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {processSteps.map((step, idx) => (
          <ProcessStep key={step.step} step={step} isVisible={isVisible.process} animationDelay={`${idx * 0.1}s`} />
        ))}
      </div>
    </div>
  </section>
);

const TestimonialCard = ({ testimonial, isVisible, animationDelay }) => (
  <div className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay }}>
    <div className="flex items-center mb-4">
      {Array.from({ length: testimonial.rating }, (_, i) => (
        <svg key={`star-${i}`} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
    <p className="text-gray-600 mb-6 italic">&quot;{testimonial.text}&quot;</p>
    <div>
      <div className="font-semibold text-gray-900">{testimonial.name}</div>
      <div className="text-blue-600">{testimonial.company}</div>
    </div>
  </div>
);

const TestimonialsSection = ({ isVisible }) => (
  <section id="testimonials" className="py-20 bg-white">
    <div className="container mx-auto px-6">
      <div className={`text-center mb-16 ${isVisible.testimonials ? 'animate-fade-in-up' : 'opacity-0'}`}>
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Client Success Stories</h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">Don't just take our word for it. Here's what our clients say about their brand transformation journey.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {testimonials.map((t, idx) => (
          <TestimonialCard key={t.name} testimonial={t} isVisible={isVisible.testimonials} animationDelay={`${idx * 0.1}s`} />
        ))}
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="py-20 bg-gradient-to-r from-red-600 to-black">
    <div className="container mx-auto px-6 text-center">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Transform Your Brand?</h2>
        <p className="text-xl text-red-100 mb-8">Join hundreds of successful businesses that have elevated their brand with our expert design services.</p>
        <div className="flex justify-center">
          <Link to="/portfolio" data-testid="cta-view-portfolio" className="bg-white text-red-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all shadow-lg inline-block">View Portfolio</Link>
        </div>
      </div>
    </div>
  </section>
);

const ContactInfo = () => (
  <div className="space-y-6">
    <div className="flex items-center space-x-4">
      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
      </div>
      <div><div className="font-semibold">Email</div><div className="text-red-600">admin@dailygrinddesign.com</div></div>
    </div>
    <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-book-call" className="flex items-center space-x-4 group">
      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center group-hover:bg-red-700 transition-colors">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
      </div>
      <div><div className="font-semibold">Book a Call</div><div className="text-red-600 group-hover:underline">Schedule on our calendar</div></div>
    </a>
    <div className="flex items-center space-x-4">
      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      </div>
      <div><div className="font-semibold">Response Time</div><div className="text-red-600">Within 24 hours</div></div>
    </div>
  </div>
);

const ContactForm = () => (
  <div className="bg-white rounded-2xl p-8 shadow-lg">
    <h3 className="text-2xl font-bold text-gray-900 mb-6">Convey Your Design Needs</h3>
    <form action="https://formspree.io/f/xwpbnaan" method="POST" className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">First Name</label>
          <input type="text" name="firstName" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all" placeholder="John" required />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Last Name</label>
          <input type="text" name="lastName" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all" placeholder="Doe" required />
        </div>
      </div>
      <div>
        <label className="block text-gray-700 font-semibold mb-2">Email</label>
        <input type="email" name="email" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all" placeholder="john@company.com" required />
      </div>
      <div>
        <label className="block text-gray-700 font-semibold mb-2">Company</label>
        <input type="text" name="company" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all" placeholder="Your Company Name" />
      </div>
      <div>
        <label className="block text-gray-700 font-semibold mb-2">Services Needed</label>
        <select name="service" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all" required defaultValue="">
          <option value="" disabled>Select a service</option>
          <option>Promotional Designs</option>
          <option>Logo Design</option>
          <option>Website Building</option>
          <option>Multiple Services</option>
        </select>
      </div>
      <div>
        <label className="block text-gray-700 font-semibold mb-2">Project Details</label>
        <textarea rows="4" name="message" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all" placeholder="Tell us about your project requirements..." required></textarea>
      </div>
      <button type="submit" className="w-full bg-red-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transform hover:scale-105 transition-all shadow-lg hover:shadow-xl">Submit Request</button>
    </form>
  </div>
);

const ContactSection = ({ isVisible }) => (
  <section id="contact" className="py-20 bg-gray-50">
    <div className="container mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-12">
        <div className={`${isVisible.contact ? 'animate-fade-in-left' : 'opacity-0'}`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Let's Create Something Amazing Together</h2>
          <p className="text-xl text-gray-600 mb-8">Ready to take your brand to the next level? Get in touch and let's discuss your project requirements.</p>
          <ContactInfo />
          <div className="mt-8">
            <img
              src="https://images.unsplash.com/photo-1699665235382-a6666f77a60e?q=80&w=1772&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Daily Grind Design Team"
              className="rounded-2xl shadow-lg w-full h-64 object-cover"
            />
          </div>
        </div>
        <div className={`${isVisible.contact ? 'animate-fade-in-right' : 'opacity-0'}`}>
          <ContactForm />
        </div>
      </div>
    </div>
  </section>
);

const Home = () => {
  const isVisible = useScrollAnimations();
  useHashScroll();

  return (
    <div className="App">
      <HeroSection isVisible={isVisible} />
      <ServicesSection isVisible={isVisible} />
      <ProcessSection isVisible={isVisible} />
      <TestimonialsSection isVisible={isVisible} />
      <CTASection />
      <ContactSection isVisible={isVisible} />
    </div>
  );
};

export default Home;
